import {
  registerOntologyMapping,
  type OntologyTypeMapping,
} from '../../../core/data-profiles/OntologyPropertyMap';

/**
 * Process domain ontology mappings — first concrete users of the
 * shared property registry. Intentionally minimal: covers the
 * Activity properties the GenerationService and ProcessRepository
 * panel already read from `node.attrs`. New ontology properties
 * (e.g. Control.owner, Risk.severity) will register here as
 * Phase D vocabulary alignment lands.
 *
 * Spec: docs/ontology-property-mapping.md
 */

const activityProcessMapping: OntologyTypeMapping = {
  ontologyType: 'activity',
  runtimeType: 'process',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'claimRefs', runtimePath: 'attrs.claimRefs', type: 'string[]', description: 'Immutable claim identifiers supporting this authorized projection.' },
    { ontologyKey: 'sourceUnitRefs', runtimePath: 'attrs.sourceUnitRefs', type: 'string[]', description: 'Source-ledger unit identifiers supporting this authorized projection.' },
    {
      ontologyKey: 'name',
      runtimePath: 'attrs.name',
      panelField: 'name',
      type: 'string',
      description: 'Display label for the activity / process step.',
    },
    {
      ontologyKey: 'owner',
      runtimePath: 'attrs.owner',
      panelField: 'owner',
      type: 'string',
      description: 'Responsible party for this activity (RACI).',
    },
    {
      ontologyKey: 'controls',
      runtimePath: 'attrs.controls',
      panelField: 'controls',
      type: 'string[]',
      description: 'Control node ids attached to this activity.',
    },
    {
      ontologyKey: 'systems',
      runtimePath: 'attrs.systems',
      panelField: 'systems',
      type: 'string[]',
      description: 'System / platform node ids the activity depends on.',
    },
    {
      ontologyKey: 'risks',
      runtimePath: 'attrs.risks',
      panelField: 'risks',
      type: 'string[]',
      description: 'Risk node ids associated with this activity.',
    },
    {
      ontologyKey: 'evidence',
      runtimePath: 'attrs.evidence',
      panelField: 'evidence',
      type: 'string[]',
      description: 'Evidence node ids that prove this activity occurred.',
    },
    {
      ontologyKey: 'description',
      runtimePath: 'attrs.description',
      panelField: 'description',
      type: 'string',
    },
    // ─── Tier 1 ontology metadata (A/B/C) ────────────────────────────
    // Per-step fields captured by EnricherService v6. Registering them
    // here is what widens the RDM hydrator's attr copy (the hydrator
    // unions these leaf keys with its legacy allowlist) AND drives panel
    // field discovery — one source of truth instead of three hand-edits.
    {
      ontologyKey: 'sla',
      runtimePath: 'attrs.sla',
      panelField: 'sla',
      type: 'string',
      description: 'Service-level / deadline governing this step (A).',
    },
    {
      ontologyKey: 'slaTrigger',
      runtimePath: 'attrs.slaTrigger',
      panelField: 'slaTrigger',
      type: 'string',
      description: 'What starts this step’s SLA clock (A).',
    },
    {
      ontologyKey: 'legalRefs',
      runtimePath: 'attrs.legalRefs',
      panelField: 'legalRefs',
      type: 'string[]',
      description: 'Regulatory / statutory citations this step invokes (B).',
    },
    {
      ontologyKey: 'decisionCriteria',
      runtimePath: 'attrs.decisionCriteria',
      panelField: 'decisionCriteria',
      type: 'string[]',
      description: 'Branch criteria behind a decision / gateway step (C).',
    },
  ],
};

let registered = false;

export function registerProcessOntologyMappings(): void {
  if (registered) return;
  registerOntologyMapping(activityProcessMapping);
  registered = true;
}
